import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const ago = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const Initial = ({ name }) => (
  <span className="h-10 w-10 shrink-0 rounded-full bg-[#b6689a]/20 text-[#b6689a] font-serif-h text-lg flex items-center justify-center">{name[0]}</span>
);

const FeedItem = ({ post, onComment }) => {
  const [text, setText] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await onComment(post.id, text);
    setText("");
  };
  return (
    <article data-testid={post.pinned ? "feed-pinned-welcome" : `feed-post-${post.id}`} className={`card-soft p-7 ${post.pinned ? "!border-[#b6689a]/60 !bg-[#b1cc90]/15" : ""}`}>
      {post.pinned && (
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#b6689a] mb-4" data-testid="feed-pinned-label"><Pin size={13} /> Pinned welcome</p>
      )}
      <div className="flex gap-4">
        <Initial name={post.name} />
        <div className="flex-1">
          <p className="text-sm"><span className="font-semibold">{post.name}</span> <span className="text-[#232b1b]/50">· {ago(post.created_at)}</span></p>
          <p className="mt-2 whitespace-pre-line leading-relaxed">{post.body}</p>
        </div>
      </div>
      <div className="mt-5 pl-14 space-y-3">
        {post.comments.map((c, i) => (
          <p key={i} className="text-sm rounded-2xl bg-[#dacac1]/35 px-4 py-3"><span className="font-semibold">{c.name}</span> {c.body}</p>
        ))}
        <form onSubmit={send} className="flex gap-2">
          <input className="field !py-2" placeholder={post.pinned ? "Introduce yourself to the circle…" : "Offer a reflection…"} value={text} onChange={(e) => setText(e.target.value)} data-testid={`feed-comment-input-${post.id}`} />
          <button className="btn btn-ghost !py-2 !px-5" data-testid={`feed-comment-btn-${post.id}`}>Reply</button>
        </form>
      </div>
    </article>
  );
};

export const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get("/feed").then((r) => setPosts(r.data));
  useEffect(() => {
    load();
  }, []);

  const share = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post("/feed", { body: text });
      setText("");
      toast.success("Your words have been shared with the circle.");
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };
  const comment = async (id, body) => {
    await api.post(`/feed/${id}/comments`, { body });
    load();
  };

  return (
    <div data-testid="members-feed">
      <span className="eyebrow">The Community Feed</span>
      <h1 className="font-serif-h font-light text-4xl sm:text-5xl text-[#577731] mt-4">A gathering place for kin</h1>
      <form onSubmit={share} className="card-soft p-6 mt-10">
        <textarea rows={3} className="field" placeholder="What is moving through you today?" value={text} onChange={(e) => setText(e.target.value)} data-testid="feed-input" />
        <div className="flex justify-end mt-4"><button className="btn btn-olive" data-testid="feed-submit-btn">Share with the Circle</button></div>
      </form>
      <div className="space-y-6 mt-8">
        {posts.length === 0 && <p className="text-[#232b1b]/60">The circle is quiet. Be the first to speak.</p>}
        {posts.map((p) => <FeedItem key={p.id} post={p} onComment={comment} />)}
      </div>
    </div>
  );
};
